import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuBtnRef = useRef<HTMLButtonElement>(null);
  const mobileOverlayRef = useRef<HTMLDivElement>(null);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsMobileCatOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }

      if (isMobileMenuOpen && mobileOverlayRef.current && !mobileOverlayRef.current.contains(target)) {
        if (mobileMenuBtnRef.current && !mobileMenuBtnRef.current.contains(target)) {
          closeMobileMenu();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [closeMobileMenu]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <button
              onClick={() => { navigate("/"); closeMobileMenu(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="flex items-center gap-2 cursor-pointer whitespace-nowrap shrink-0"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-table-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-base md:text-xl font-bold text-foreground-950">
                USFans Spreadsheet
              </span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <button
                onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("breadcrumb.home")}
              </button>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("nav.usfans")}
              </a>

              {/* Categories Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>{t("nav.categories")}</span>
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-7 h-7 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  navigate("/artykul/usfans-spreadsheet-przewodnik");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("nav.guide")}
              </button>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium border border-primary-500 text-primary-600 px-4 py-1.5 rounded-full hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("nav.shoppingGuide")}
              </a>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="hidden lg:inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("nav.buyNow")}
              </a>

              {/* Mobile hamburger */}
              <button
                ref={mobileMenuBtnRef}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg hover:bg-background-100 transition-colors"
                aria-label={isMobileMenuOpen ? "Zamknij menu" : "Otwórz menu"}
              >
                <i
                  className={`text-xl text-foreground-950 transition-all duration-200 ${
                    isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"
                  }`}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay — rendered OUTSIDE the nav to avoid fixed-in-fixed stacking issues */}
      {isMobileMenuOpen && (
        <div
          ref={mobileOverlayRef}
          className="lg:hidden fixed inset-0 z-[60]"
          style={{ top: "64px" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20"
            onClick={closeMobileMenu}
          ></div>

          {/* Menu panel */}
          <div className="relative w-full h-full overflow-y-auto bg-background-50">
            <div className="flex flex-col min-h-full">
              <div className="p-5 space-y-1">
                {/* Home */}
                <button
                  onClick={() => {
                    navigate("/");
                    closeMobileMenu();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-home-line text-lg text-foreground-700"></i>
                  </div>
                  <span>{t("breadcrumb.home")}</span>
                </button>

                {/* Categories toggle */}
                <button
                  onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                      <i className="ri-apps-2-line text-lg text-foreground-700"></i>
                    </div>
                    <span>{t("nav.categories")}</span>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 ${
                      isMobileCatOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {/* Categories list */}
                {isMobileCatOpen && (
                  <div className="ml-5 pl-4 border-l-2 border-background-200/60 space-y-0.5 py-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobileMenu}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-600 active:bg-background-200 transition-colors cursor-pointer"
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`${cat.icon} text-sm text-foreground-500`}></i>
                        </div>
                        <span className="text-xs">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}

                {/* USFans */}
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-global-line text-lg text-foreground-700"></i>
                  </div>
                  <span>{t("nav.usfans")}</span>
                </a>

                {/* Guide */}
                <button
                  onClick={() => {
                    navigate("/artykul/usfans-spreadsheet-przewodnik");
                    closeMobileMenu();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-book-open-line text-lg text-foreground-700"></i>
                  </div>
                  <span>{t("nav.guide")}</span>
                </button>

                {/* Shopping Guide */}
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-shopping-bag-line text-lg text-foreground-700"></i>
                  </div>
                  <span>{t("nav.shoppingGuide")}</span>
                </a>
              </div>

              {/* Buy Now CTA at bottom */}
              <div className="p-5 pt-0 mt-auto">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 active:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("nav.buyNow")}
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}